import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloudOff, Clock, RefreshCw, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatDate } from "@/lib/api";
import { discardPending, syncPending, useOnline, usePending } from "@/lib/offline";
import { cacheQuizImages } from "@/components/quiz/QuizImage";

export function useSync() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  return useCallback(() => syncPending(user.id, (n) => {
    toast.success(`${n} offline session${n > 1 ? "s" : ""} organised and saved`);
    qc.invalidateQueries();
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  }), [user.id, qc, setUser]);
}

export function SyncManager() {
  const { user } = useAuth();
  const pending = usePending(user.id);
  const online = useOnline();
  const sync = useSync();
  const has = pending.length > 0;

  const qc = useQueryClient();
  useEffect(() => {
    if (!online) return;
    const warm = [
      [["sessions", ""], "/sessions?limit=200"], [["tags"], "/tags"], [["insights"], "/insights"], [["stats"], "/profile/stats"],
      [["quiz-overview"], "/quizzes/overview"], [["offline-pack"], "/quizzes/offline-pack"], [["quiz-history"], "/quizzes/history?limit=100"],
      [["leaderboard", "all"], "/leaderboard?kind=all"], [["leaderboard", "week"], "/leaderboard?kind=week"],
    ];
    const t = setTimeout(() => {
      warm.forEach(([queryKey, url]) => qc.prefetchQuery({ queryKey, queryFn: () => api.get(url).then((r) => r.data), staleTime: 5 * 60_000 }));
      qc.fetchQuery({ queryKey: ["offline-pack"], queryFn: () => api.get("/quizzes/offline-pack").then((r) => r.data), staleTime: 5 * 60_000 })
        .then((pack) => cacheQuizImages(pack.questions)).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, [online, qc]);

  useEffect(() => {
    if (online && has) sync();
  }, [online, has, sync]);

  useEffect(() => {
    if (!has) return;
    const t = setInterval(sync, 60_000);
    return () => clearInterval(t);
  }, [has, sync]);

  return null;
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="sticky top-0 z-40 bg-[#161618]/95 backdrop-blur-md border-b border-[#27272A] px-5 py-2.5 flex items-center gap-2.5 text-xs text-[#D4D4D8]" style={{ paddingTop: "max(env(safe-area-inset-top), 0.625rem)" }} data-testid="offline-banner">
      <CloudOff size={14} className="text-[#F59E0B] shrink-0" />
      <span>You're offline. Showing your last synced data. New logs save on this phone.</span>
    </div>
  );
}

const STATUS = {
  pending: { icon: Clock, label: "Pending sync", color: "#F59E0B" },
  syncing: { icon: Loader2, label: "Syncing…", color: "#3B82F6", spin: true },
  limit: { icon: Clock, label: "Waiting: daily AI limit reached, retries automatically", color: "#F59E0B" },
  failed: { icon: AlertTriangle, label: "Couldn't sync", color: "#EF4444" },
};

export function PendingList() {
  const { user } = useAuth();
  const pending = usePending(user.id);
  const online = useOnline();
  const sync = useSync();
  if (!pending.length) return null;

  return (
    <section className="space-y-2.5" data-testid="pending-list">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Saved on this phone</div>
        {online && (
          <button data-testid="pending-sync-now" onClick={sync} className="flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white transition-colors">
            <RefreshCw size={13} /> Sync now
          </button>
        )}
      </div>
      {pending.map((p) => {
        const st = STATUS[p.status] || STATUS.pending;
        const Icon = st.icon;
        return (
          <div key={p.id} className="card p-4 border-dashed" data-testid={`pending-item-${p.id}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs text-[#71717A]">{formatDate(p.date)}</span>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: st.color }} data-testid="pending-status">
                <Icon size={12} className={st.spin ? "animate-spin" : ""} /> {st.label}
              </span>
            </div>
            <p className="text-sm text-[#D4D4D8] line-clamp-3 leading-relaxed">{p.text}</p>
            {p.status === "failed" && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-[#FCA5A5]">{p.error}</span>
                <button data-testid="pending-discard" onClick={() => window.confirm("Delete this unsynced note from your phone? This can't be undone.") && discardPending(p.id)} className="shrink-0 p-2 text-[#71717A] hover:text-[#FF3B30] transition-colors" aria-label="Delete note">
                  <Trash2 size={15} />
                </button>
              </div>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-[#71717A]">Notes stay safely on this phone until they're saved to your journal. The AI organises them as soon as you're back online.</p>
    </section>
  );
}
